import { useEffect } from 'react';

interface PrivacyModalProps {
  onClose: () => void;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-white text-sm font-semibold mb-2">{title}</h3>
      <div className="text-white/60 text-xs leading-relaxed space-y-2">{children}</div>
    </div>
  );
}

function InfoBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-white/5 rounded-lg p-4 text-xs leading-relaxed space-y-1.5">
      {children}
    </div>
  );
}

export default function PrivacyModal({ onClose }: PrivacyModalProps) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 bg-[#2C2825] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl">

        {/* Header */}
        <div className="flex items-center justify-between px-8 py-5 border-b border-white/10 flex-shrink-0">
          <h2 className="text-white text-lg font-medium tracking-wide">개인정보 처리방침</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-white text-lg"></i>
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto px-8 py-6 space-y-7">

          <div className="text-white/40 text-xs leading-relaxed">
            <p className="mb-1">시행일: 2024년 01월 01일 &nbsp;|&nbsp; 최종 개정일: 2025년 01월 01일</p>
            <p>
              주식회사 위딘(이하 &quot;회사&quot;)은 「개인정보 보호법」, 「정보통신망 이용촉진 및 정보보호 등에 관한 법률」 등 관련 법령을 준수하며,
              정보주체의 개인정보 보호 및 권익을 보호하기 위해 다음과 같이 개인정보 처리방침을 수립·공개합니다.
            </p>
          </div>

          {/* 1 */}
          <Section title="제1조 (개인정보의 처리 목적)">
            <p>회사는 아래 목적을 위하여 개인정보를 처리하며, 해당 목적 이외의 용도로 이용하지 않습니다. 이용 목적이 변경될 경우 사전에 동의를 받겠습니다.</p>
            <ul className="list-disc list-inside space-y-1 mt-1">
              <li>회원 가입·관리 및 본인 인증</li>
              <li>상품 주문·결제·배송 및 환불 처리</li>
              <li>고객 상담, 불만 접수 및 처리</li>
              <li>마케팅·광고 활용 및 신규 서비스 개발 (별도 동의 시)</li>
              <li>서비스 품질 개선 및 통계 분석</li>
              <li>부정거래 탐지 및 이용약관 위반 행위 방지</li>
            </ul>
          </Section>

          {/* 2 */}
          <Section title="제2조 (수집하는 개인정보 항목 및 수집 방법)">
            <p>회사는 서비스 제공을 위해 아래 개인정보를 수집합니다.</p>
            <InfoBox>
              <div>
                <p className="text-white/80 font-medium mb-1">필수 항목</p>
                <p>이름, 이메일 주소, 비밀번호(암호화 저장), 휴대전화번호, 생년월일, 성별, 배송지 주소</p>
              </div>
              <div>
                <p className="text-white/80 font-medium mb-1">선택 항목</p>
                <p>마케팅 수신 동의 여부, 선호 카테고리</p>
              </div>
              <div>
                <p className="text-white/80 font-medium mb-1">자동 수집 항목</p>
                <p>IP 주소, 쿠키, 기기 정보(OS, 브라우저), 서비스 이용 기록, 접속 일시</p>
              </div>
            </InfoBox>
            <p className="mt-1">수집 방법: 홈페이지 회원가입 및 서비스 이용 과정의 자동 수집</p>
          </Section>

          {/* 3 */}
          <Section title="제3조 (개인정보의 처리 및 보유 기간)">
            <p>회사는 법령 및 정보주체 동의에 따른 보유 기간 내에서 개인정보를 처리합니다.</p>
            <InfoBox>
              <div className="space-y-1.5">
                <div className="flex gap-3"><span className="text-white/50 w-40 flex-shrink-0">회원 정보</span><span>회원 탈퇴 즉시 삭제 (법령 보존 의무 제외)</span></div>
                <div className="flex gap-3"><span className="text-white/50 w-40 flex-shrink-0">계약·청약철회 기록</span><span>5년 (전자상거래법)</span></div>
                <div className="flex gap-3"><span className="text-white/50 w-40 flex-shrink-0">대금 결제·공급 기록</span><span>5년 (전자상거래법)</span></div>
                <div className="flex gap-3"><span className="text-white/50 w-40 flex-shrink-0">소비자 불만·분쟁 기록</span><span>3년 (전자상거래법)</span></div>
                <div className="flex gap-3"><span className="text-white/50 w-40 flex-shrink-0">표시·광고 기록</span><span>6개월 (전자상거래법)</span></div>
                <div className="flex gap-3"><span className="text-white/50 w-40 flex-shrink-0">접속 로그·이용 기록</span><span>3개월 (통신비밀보호법)</span></div>
              </div>
            </InfoBox>
          </Section>

          {/* 4 */}
          <Section title="제4조 (개인정보의 제3자 제공)">
            <p>회사는 원칙적으로 정보주체의 개인정보를 외부에 제공하지 않습니다. 단, 아래 경우에는 예외입니다.</p>
            <ul className="list-disc list-inside space-y-1 mt-1">
              <li>정보주체가 사전에 명시적으로 동의한 경우</li>
              <li>법령 규정 또는 수사기관의 적법한 요청이 있는 경우</li>
              <li>서비스 계약 이행을 위해 불가피하게 필요한 경우 (최소 범위)</li>
            </ul>
            <p className="mt-1">제3자 제공 시 제공 대상, 목적, 항목, 보유 기간을 사전 고지하고 별도 동의를 받겠습니다.</p>
          </Section>

          {/* 5 */}
          <Section title="제5조 (개인정보 처리 위탁)">
            <p>원활한 서비스 제공을 위해 아래와 같이 개인정보 처리 업무를 위탁합니다. 위탁 업체 변경 시 즉시 공지합니다.</p>
            <InfoBox>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-white/70 border-b border-white/10">
                    <th className="text-left pb-2 font-medium w-1/3">수탁 업체</th>
                    <th className="text-left pb-2 font-medium w-1/3">위탁 업무</th>
                    <th className="text-left pb-2 font-medium">보유·이용 기간</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  <tr>
                    <td className="py-2 pr-2">결제 대행사(PG사)</td>
                    <td className="py-2 pr-2">결제 처리 및 본인 인증</td>
                    <td className="py-2">계약 종료 시까지</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-2">택배·배송 업체</td>
                    <td className="py-2 pr-2">상품 배송 및 배송 현황 관리</td>
                    <td className="py-2">배송 완료 후 즉시</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-2">SMS·알림 발송 업체</td>
                    <td className="py-2 pr-2">주문 확인·배송 안내 발송</td>
                    <td className="py-2">발송 완료 후 즉시</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-2">클라우드 호스팅 업체</td>
                    <td className="py-2 pr-2">서버 인프라 운영</td>
                    <td className="py-2">계약 종료 시까지</td>
                  </tr>
                </tbody>
              </table>
            </InfoBox>
          </Section>

          {/* 6 */}
          <Section title="제6조 (정보주체의 권리·의무 및 행사 방법)">
            <p>정보주체는 회사에 언제든지 아래 권리를 행사할 수 있으며, 법정 대리인도 동일한 권리를 행사할 수 있습니다.</p>
            <ul className="list-disc list-inside space-y-1 mt-1">
              <li>개인정보 처리 현황 열람 요구</li>
              <li>오류가 있는 경우 정정·삭제 요구</li>
              <li>개인정보 처리 정지 요구</li>
              <li>개인정보 이동권 요구 (기계 판독 가능한 형태로 제공)</li>
            </ul>
            <p className="mt-1">
              권리 행사는 고객센터(cs@within.co.kr)를 통해 서면, 이메일로 요청할 수 있으며, 회사는 10일 이내에 조치합니다.
              단, 법령상 의무로 보존해야 하는 경우에는 처리가 제한될 수 있습니다.
            </p>
          </Section>

          {/* 7 */}
          <Section title="제7조 (개인정보의 파기 절차 및 방법)">
            <p>회사는 보유 기간이 경과하거나 처리 목적이 달성된 개인정보는 지체 없이 파기합니다.</p>
            <InfoBox>
              <div className="space-y-1.5">
                <div className="flex gap-3"><span className="text-white/50 w-28 flex-shrink-0">전자적 파일</span><span>복구 불가능한 기술적 방법(Secure Delete)으로 영구 삭제</span></div>
                <div className="flex gap-3"><span className="text-white/50 w-28 flex-shrink-0">종이 문서</span><span>분쇄 또는 소각 처리</span></div>
              </div>
            </InfoBox>
            <p className="mt-1">단, 법령에 따라 보존 의무가 있는 정보는 별도 데이터베이스(DB)로 분리 보관하며, 다른 목적으로는 절대 이용하지 않습니다.</p>
          </Section>

          {/* 8 */}
          <Section title="제8조 (쿠키(Cookie)의 운용 및 거부)">
            <p>
              회사는 이용자에게 최적화된 서비스 제공을 위해 쿠키를 사용합니다.
              쿠키는 웹서버가 브라우저에 전송하는 소량의 정보로, 이용자 PC에 저장됩니다.
            </p>
            <InfoBox>
              <div className="space-y-1.5">
                <div className="flex gap-3"><span className="text-white/50 w-24 flex-shrink-0">운용 목적</span><span>자동 로그인 유지, 최근 본 상품, 개인화 추천, 이용 패턴 분석</span></div>
                <div className="flex gap-3"><span className="text-white/50 w-24 flex-shrink-0">거부 방법</span><span>브라우저 설정 &gt; 쿠키 차단 또는 삭제 (단, 일부 서비스 이용 제한 가능)</span></div>
              </div>
            </InfoBox>
          </Section>

          {/* 9 */}
          <Section title="제9조 (개인정보의 안전성 확보 조치)">
            <p>회사는 개인정보의 안전성 확보를 위해 아래 조치를 시행합니다.</p>
            <ul className="list-disc list-inside space-y-1 mt-1">
              <li>관리적 조치: 내부 개인정보 관리계획 수립·시행, 직원 정기 교육</li>
              <li>기술적 조치: 개인정보 처리 시스템 접근 권한 최소화, 접속 기록 보관 및 위·변조 방지, 데이터 암호화(비밀번호, 결제 정보 등), 악성 프로그램 방지 소프트웨어 설치</li>
              <li>물리적 조치: 전산실 및 자료보관실 접근 통제</li>
            </ul>
          </Section>

          {/* 10 */}
          <Section title="제10조 (개인정보 보호책임자 및 문의처)">
            <p>회사는 개인정보 처리에 관한 업무를 총괄하여 책임지는 개인정보 보호책임자를 지정합니다.</p>
            <InfoBox>
              <div className="space-y-1.5">
                <div className="flex gap-3"><span className="text-white/50 w-28 flex-shrink-0">회사명</span><span>주식회사 위딘</span></div>
                <div className="flex gap-3"><span className="text-white/50 w-28 flex-shrink-0">대표자</span><span>전가혜</span></div>
                <div className="flex gap-3"><span className="text-white/50 w-28 flex-shrink-0">주소</span><span>인천광역시 연수구 인천타워대로 323, 에이동 31층 에스 216호</span></div>
                <div className="flex gap-3"><span className="text-white/50 w-28 flex-shrink-0">개인정보 문의</span><span>cs@within.co.kr</span></div>
                <div className="flex gap-3"><span className="text-white/50 w-28 flex-shrink-0">고객센터</span><span>1644-0000 (평일 10:00 – 17:00)</span></div>
              </div>
            </InfoBox>
            <p className="mt-2">개인정보 침해 관련 신고·상담은 아래 기관에 문의하실 수 있습니다.</p>
            <ul className="list-disc list-inside space-y-1 mt-1">
              <li>개인정보 분쟁조정위원회 (kopico.go.kr / 1833-6972)</li>
              <li>개인정보 침해신고센터 (privacy.kisa.or.kr / ☎ 118)</li>
              <li>대검찰청 사이버수사과 (spo.go.kr / ☎ 02-3480-3573)</li>
              <li>경찰청 사이버수사국 (ecrm.cyber.go.kr / ☎ 182)</li>
            </ul>
          </Section>

          {/* 11 */}
          <Section title="제11조 (개인정보 처리방침의 변경)">
            <p>
              본 개인정보 처리방침은 법령·정책 또는 서비스 변경에 따라 개정될 수 있습니다.
              내용이 변경될 경우 시행 7일(중요 사항은 30일) 전부터 홈페이지 공지사항 또는 이메일을 통해 사전 안내합니다.
              변경 이후에도 서비스를 계속 이용하면 변경된 내용에 동의한 것으로 간주합니다.
            </p>
          </Section>

          <p className="text-white/30 text-xs pt-4 border-t border-white/10">
            본 개인정보 처리방침은 2025년 01월 01일부터 적용됩니다. &nbsp;|&nbsp; 주식회사 위딘 대표이사 전가혜
          </p>
        </div>

        {/* Footer */}
        <div className="px-8 py-5 border-t border-white/10 flex-shrink-0">
          <button
            onClick={onClose}
            className="w-full bg-[#8B7355] text-white py-3 rounded-full text-sm font-medium hover:bg-[#7A6548] transition-colors cursor-pointer whitespace-nowrap"
          >
            확인
          </button>
        </div>
      </div>
    </div>
  );
}
